import { doc, getDoc, onSnapshot, serverTimestamp, setDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from './config';
import { getDateKey } from '../games/WordMaster/wordMasterUtils';
import { advanceAfterPlay } from '../utils/calendarStreak';
import { updateLeaderboardEntry } from './leaderboardService';

function isOfflineError(err) {
  return err?.code === 'unavailable' || err?.message?.includes('offline');
}

function dailyPlayRef(dateId, uid) {
  return doc(db, 'dailyLetterHive', dateId, 'plays', uid);
}

function readGlobalStreakFields(data) {
  const current = (data.globalStreak ?? 0) | 0;
  const lastPlayed = data.lastPlayedGlobalDate ?? null;
  return { current, lastPlayed };
}

/**
 * Merge-save daily Letter Hive play (matches Flutter `LetterHiveRepository.saveDailyPlay`).
 */
export async function getLetterHiveDailyPlaySnapshot(uid, dateId = getDateKey()) {
  try {
    const snap = await getDoc(dailyPlayRef(dateId, uid));
    if (!snap.exists()) return null;
    const d = snap.data();
    const words = d.foundWords;
    const score = d.totalScore;
    const hiveKey = d.hiveKey;
    if (!Array.isArray(words) || typeof score !== 'number') return null;
    return {
      foundWords: words.map((e) => String(e).toUpperCase()),
      totalScore: score,
      hiveKey: typeof hiveKey === 'string' ? hiveKey : '',
    };
  } catch (err) {
    if (isOfflineError(err)) return null;
    throw err;
  }
}

export async function saveLetterHiveDailyPlay({
  uid,
  dateId,
  foundWords,
  totalScore,
  hiveKey,
  displayName,
}) {
  const playRef = dailyPlayRef(dateId, uid);
  await setDoc(
    playRef,
    {
      foundWords,
      totalScore,
      hiveKey,
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );
}

/**
 * Commit daily Letter Hive progress to profile + leaderboards (intentionally higher-latency).
 * Call this sparingly (e.g. debounced), not on every found word.
 */
export async function commitLetterHiveDailyResult({
  uid,
  dateId,
  totalScore,
  displayName,
}) {
  const userRef = doc(db, 'users', uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) return { ok: false, reason: 'no_profile' };

  const data = snap.data();
  const xs = { ...(data.letterHiveStats || {}) };
  const prevBest = (xs.bestScore ?? 0) | 0;

  const { current, lastPlayed } = readGlobalStreakFields(data);
  const global = advanceAfterPlay({
    playDate: dateId,
    lastPlayed,
    currentStreak: current,
  });

  const didImprove = totalScore > prevBest;
  const didAdvanceStreak = global.streak > current || global.lastPlayed !== lastPlayed;

  if (totalScore > 0) {
    xs.lastDailyCompletedDate = dateId;
  }
  if (didImprove) {
    xs.bestScore = totalScore;
  }

  const name = displayName || data.displayName || 'Player';

  await updateDoc(userRef, {
    letterHiveStats: xs,
    ...(didAdvanceStreak
      ? {
          globalStreak: global.streak,
          lastPlayedGlobalDate: global.lastPlayed,
        }
      : {}),
    updatedAt: serverTimestamp(),
  });

  if (didImprove) {
    await updateLeaderboardEntry('letter_hive_best', uid, name, totalScore);
  }
  if (didAdvanceStreak) {
    await updateLeaderboardEntry('global_streak', uid, name, global.streak);
  }

  return { ok: true, improved: didImprove, advancedStreak: didAdvanceStreak };
}

/**
 * Subscribe to today's play doc. Returns unsubscribe.
 */
export function subscribeLetterHiveDailyPlay(callback) {
  const user = auth.currentUser;
  if (!user) {
    callback(null);
    return () => {};
  }
  const dateId = getDateKey();
  const ref = dailyPlayRef(dateId, user.uid);
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) {
        callback(null);
        return;
      }
      const d = snap.data();
      const words = d.foundWords;
      const score = d.totalScore;
      const hiveKey = d.hiveKey;
      if (!Array.isArray(words) || typeof score !== 'number') {
        callback(null);
        return;
      }
      callback({
        foundWords: words.map((e) => String(e).toUpperCase()),
        totalScore: score,
        hiveKey: typeof hiveKey === 'string' ? hiveKey : '',
      });
    },
    (err) => {
      if (!isOfflineError(err)) console.error('Letter Hive daily play:', err);
      callback(null);
    }
  );
}
